// Generates a branded, shareable PNG of a route — drawn entirely on a canvas
// from the route geometry (no map tiles / API). Returns a Blob.

import QRCode from 'qrcode';
import { computeCurviness } from './curviness';
import { formatDistance, formatDuration } from './formatters';

const FONT = '"Plus Jakarta Sans", -apple-system, system-ui, sans-serif';

export async function generateRouteCard({ name, geometry, distance, duration, url }) {
  // Make sure the brand font is ready so canvas text renders correctly.
  try { await document.fonts?.ready; } catch { /* ignore */ }

  const S = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');

  // Background: warm orange gradient + bottom vignette for text legibility.
  const bg = ctx.createLinearGradient(0, 0, S, S);
  bg.addColorStop(0, '#ff8a33');
  bg.addColorStop(1, '#f26a06');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, S, S);
  const vg = ctx.createLinearGradient(0, S * 0.45, 0, S);
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(1, 'rgba(0,0,0,0.30)');
  ctx.fillStyle = vg;
  ctx.fillRect(0, S * 0.45, S, S * 0.55);

  // Wordmark
  ctx.textBaseline = 'top';
  ctx.fillStyle = 'rgba(255,255,255,0.96)';
  ctx.font = `800 46px ${FONT}`;
  ctx.fillText('Motoroam', 64, 56);
  ctx.fillStyle = 'rgba(255,255,255,0.82)';
  ctx.font = `600 24px ${FONT}`;
  ctx.fillText('MOTORCYCLE ROUTE', 66, 116);

  // Route silhouette
  const coords = geometry?.coordinates || [];
  if (coords.length >= 2) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [lng, lat] of coords) {
      if (lng < minX) minX = lng; if (lng > maxX) maxX = lng;
      if (lat < minY) minY = lat; if (lat > maxY) maxY = lat;
    }
    const boxX = 130, boxY = 210, boxW = S - 260, boxH = 560;
    const midLat = ((minY + maxY) / 2) * Math.PI / 180;
    const aspect = Math.cos(midLat) || 1; // longitude compression at latitude
    const spanX = Math.max(1e-6, (maxX - minX) * aspect);
    const spanY = Math.max(1e-6, maxY - minY);
    const scale = Math.min(boxW / spanX, boxH / spanY);
    const drawW = spanX * scale, drawH = spanY * scale;
    const offX = boxX + (boxW - drawW) / 2;
    const offY = boxY + (boxH - drawH) / 2;
    const toXY = ([lng, lat]) => [offX + (lng - minX) * aspect * scale, offY + (maxY - lat) * scale];

    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const trace = () => {
      ctx.beginPath();
      coords.forEach((c, i) => { const [x, y] = toXY(c); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
    };
    ctx.strokeStyle = 'rgba(255,255,255,0.28)'; ctx.lineWidth = 24; trace(); ctx.stroke();
    ctx.strokeStyle = '#ffffff';                ctx.lineWidth = 10; trace(); ctx.stroke();

    const dot = (pt, color) => {
      const [x, y] = toXY(pt);
      ctx.fillStyle = color; ctx.beginPath(); ctx.arc(x, y, 15, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 5; ctx.beginPath(); ctx.arc(x, y, 15, 0, Math.PI * 2); ctx.stroke();
    };
    dot(coords[0], '#00c2a8');
    dot(coords[coords.length - 1], '#f5564a');
  }

  // Route name (wrapped, max 2 lines)
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#ffffff';
  ctx.font = `800 64px ${FONT}`;
  wrapText(ctx, name || 'My Route', 64, 880, S - 128, 70, 2);

  // Stat line
  const c = computeCurviness(geometry);
  const parts = [];
  if (distance != null) parts.push(formatDistance(distance));
  if (duration != null) parts.push(formatDuration(duration));
  if (c) parts.push(c.label);
  ctx.fillStyle = 'rgba(255,255,255,0.94)';
  ctx.font = `600 36px ${FONT}`;
  ctx.fillText(parts.join('   ·   '), 64, S - 70);

  // QR code (top-right) so anyone who sees the image can open this exact route.
  if (url) {
    try {
      const qrDataUrl = await QRCode.toDataURL(url, { margin: 1, width: 320, color: { dark: '#1e2430', light: '#ffffff' } });
      const qrImg = await loadImage(qrDataUrl);
      const panelW = 190, panelH = 216, px = S - 64 - panelW, py = 52, qrSize = 152;
      ctx.fillStyle = '#ffffff';
      roundRect(ctx, px, py, panelW, panelH, 18);
      ctx.fill();
      ctx.drawImage(qrImg, px + (panelW - qrSize) / 2, py + 16, qrSize, qrSize);
      ctx.fillStyle = '#1e2430';
      ctx.font = `800 19px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.fillText('SCAN TO RIDE', px + panelW / 2, py + qrSize + 46);
      ctx.textAlign = 'left';
    } catch { /* QR is optional */ }
  }

  return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/png', 0.92));
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxW, lineH, maxLines) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) break;
    } else {
      line = test;
    }
  }
  // remaining words onto the last line, ellipsised if needed
  const used = lines.join(' ');
  let rest = String(text).slice(used.length).trim();
  if (!rest) rest = line;
  while (rest && ctx.measureText(rest + '…').width > maxW) rest = rest.slice(0, -1);
  if (lines.length === maxLines - 1 && ctx.measureText(rest).width >= maxW) rest += '…';
  lines.push(rest);

  const start = y - (lines.length - 1) * lineH;
  lines.forEach((l, i) => ctx.fillText(l, x, start + i * lineH));
}
